import React, { useEffect, useMemo, useState } from 'react';
import { getMyLandlords } from '../../../api/services/agentService';
import './Landlords.css';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const Landlords = () => {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchMyLandlords = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyLandlords();
        const list = response?.data || [];
        setLandlords(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching landlords:', error);
        setErrorMessage(error.message || 'Failed to load landlords. Please try again.');
        setLandlords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMyLandlords();
  }, []);

  const visibleLandlords = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return landlords;

    return landlords.filter((landlord) => {
      const haystacks = [
        landlord?.landlordName,
        landlord?.landlordEmail,
        landlord?.landlordPhone,
        landlord?.landlordId,
        ...(Array.isArray(landlord?.properties)
          ? landlord.properties.map((p) => p?.propertyName)
          : []),
      ];
      return haystacks.some((v) => normalizeText(v).includes(q));
    });
  }, [landlords, search]);

  return (
    <div className="agent-landlords-container">
      <div className="agent-landlords-header">
        <div>
          <h2>My Landlords</h2>
          <p className="agent-landlords-subtitle">
            Landlords linked to you and their properties.
          </p>
        </div>

        <div className="agent-landlords-search-wrap">
          <input
            type="text"
            className="agent-landlords-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, property..."
          />
        </div>
      </div>

      {errorMessage && (
        <div className="empty-state" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="empty-state">Loading landlords...</div>
      ) : visibleLandlords.length === 0 ? (
        <div className="empty-state">
          {search ? 'No landlords match your search.' : 'No landlords linked to your account yet.'}
        </div>
      ) : (
        <div className="agent-landlords-grid">
          {visibleLandlords.map((landlord) => {
            const properties = Array.isArray(landlord?.properties) ? landlord.properties : [];
            return (
              <div key={landlord.landlordId || landlord.landlordEmail} className="agent-landlord-card card">
                <div className="agent-landlord-head">
                  <h3>{landlord?.landlordName || 'Landlord'}</h3>
                </div>

                <div className="agent-landlord-meta">
                  <div className="agent-landlord-meta-row">
                    <span className="agent-landlord-meta-label">Email</span>
                    <span className="agent-landlord-meta-value">{landlord?.landlordEmail || '—'}</span>
                  </div>
                  <div className="agent-landlord-meta-row">
                    <span className="agent-landlord-meta-label">Phone</span>
                    <span className="agent-landlord-meta-value">{landlord?.landlordPhone || '—'}</span>
                  </div>
                </div>

                <div className="agent-landlord-properties">
                  <div className="agent-landlord-properties-title">
                    Properties ({properties.length})
                  </div>
                  {properties.length === 0 ? (
                    <div className="agent-landlord-empty-props">No properties listed.</div>
                  ) : (
                    <div className="agent-landlord-props-list">
                      {properties.map((property) => (
                        <span
                          key={property?.propertyId || property?.propertyName}
                          className="agent-landlord-prop-chip"
                        >
                          {property?.propertyName || 'Property'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Landlords;
